import type { PhysicsTuning } from '../config/tuning';
import { decodeInputs, NEUTRAL_INPUT, unpackInput } from '../sim/input';
import type { TrackRuntime } from '../sim/trackQuery';
import { type SimEvent, type SimState, spawnState, stepWorld } from '../sim/world';
import type { LapRecord } from './replay';

/** How much run-up to the line the replay shows before the timed lap (ticks). */
export const RUN_UP = 90;
/** After the line the car rolls on this long, then the replay ends (ticks). */
const ROLL_OUT = 150;

/**
 * Watching a recorded lap: the same simulation, fed the recording's inputs one
 * tick at a time, so the car does exactly what it did (skids, kerbs and all).
 * It skips through the run to a moment before the lap, then plays it in real
 * time. Nothing here touches your own laps.
 *
 * Or (no recording) a friend driving right now: their run from the grid, its
 * inputs fed in as they arrive (feed), each tick played once it's here.
 */
export class Spectator {
  readonly physics: PhysicsTuning;
  state: SimState;
  /** Pose at the previous tick, for render interpolation. */
  prevX = 0;
  prevY = 0;
  prevHeading = 0;
  prevS = 0;
  /** The lap as the replay timed it, once the car crosses the line. */
  finished: { ms: number; valid: boolean; tick: number } | null = null;
  /** Nothing more to show. */
  done = false;
  private packed: number[];
  private events: SimEvent[] = [];
  /** Watching live: what each tick played set off (laps, sectors, track limits), unless skipping ahead. */
  onEvents: ((events: readonly SimEvent[]) => void) | null = null;
  private quiet = false;

  /**
   * `runUp`: ticks shown before the lap starts. Laps watched together (a podium)
   * share one run-up, so they all cross the line at the same moment.
   */
  constructor(
    private rt: TrackRuntime,
    physics: PhysicsTuning,
    private record: LapRecord | null,
    runUp = RUN_UP,
  ) {
    this.physics = physics;
    this.packed = record ? decodeInputs(record.inputs) : [];
    this.state = spawnState(rt, physics);
    if (record) {
      // Where the lap starts, then a run-up before it.
      const lapTick = Spectator.lapStartTick(rt, physics, record, this.packed);
      const from = Math.max(0, lapTick - runUp);
      while (this.state.tick < from) this.advance();
    }
    this.keepPrev();
  }

  /** A friend's run as they drive it: from the grid, nothing to play until their inputs arrive. */
  static live(rt: TrackRuntime, physics: PhysicsTuning): Spectator {
    return new Spectator(rt, physics, null);
  }

  /**
   * Live: their inputs from tick `from` on (any already here are the same: kept). A piece that would leave a gap
   * is dropped (false): the run is asked for again.
   */
  feed(from: number, inputs: readonly number[]): boolean {
    if (this.record || from > this.packed.length) return false;
    for (let i = 0; i < inputs.length; i++) this.packed[from + i] = inputs[i];
    return true;
  }

  /** Live: ticks here and not played yet. */
  get ahead(): number {
    return this.packed.length - this.state.tick;
  }

  /** Live: play on without showing it (quietly: no laps or sectors announced) until `keep` ticks are left ahead, `max` at most. */
  skipTo(keep: number, max: number): void {
    this.quiet = true;
    for (let n = 0; n < max && this.ahead > keep; n++) this.advance();
    this.quiet = false;
    this.keepPrev();
  }

  /** The tick (since spawn) at which a recording's timed lap starts (0 if it never does). */
  static lapStartTick(rt: TrackRuntime, physics: PhysicsTuning, record: LapRecord, packed = decodeInputs(record.inputs)): number {
    const probe = spawnState(rt, physics);
    const events: SimEvent[] = [];
    for (let i = 0; i < packed.length; i++) {
      events.length = 0;
      stepWorld(probe, unpackInput(packed[i]), rt, physics, events);
      if (events.some((e) => e.type === 'lapStart' && e.lapIndex === record.lapIndex)) return probe.tick;
    }
    return 0;
  }

  /** The tick the replay started at (a little before the lap). */
  get startTick(): number {
    return this.state.tick;
  }

  step(): void {
    if (this.done) return;
    this.keepPrev();
    // Live, and their next inputs aren't here yet: the car waits (held still).
    if (!this.record && this.ahead <= 0) return;
    this.advance();
    if (this.finished && this.state.tick >= this.finished.tick + ROLL_OUT) this.done = true;
  }

  private keepPrev(): void {
    const c = this.state.car;
    this.prevX = c.x;
    this.prevY = c.y;
    this.prevHeading = c.heading;
    this.prevS = c.s;
  }

  private advance(): void {
    const t = this.state.tick;
    // Past the end of the recording the car just rolls.
    const input = t < this.packed.length ? unpackInput(this.packed[t]) : NEUTRAL_INPUT;
    this.events.length = 0;
    stepWorld(this.state, input, this.rt, this.physics, this.events);
    const record = this.record;
    if (!record) {
      if (this.events.length && !this.quiet) this.onEvents?.(this.events);
      return;
    }
    for (const e of this.events) {
      if (e.type === 'lapComplete' && e.lapIndex === record.lapIndex && !this.finished) this.finished = { ms: e.ms, valid: e.valid, tick: e.endTick };
    }
  }

  // --- the same queries the game asks of a live session

  lapElapsedMs(renderTick: number): number | null {
    if (this.finished) return this.finished.ms;
    const t = this.state.timing;
    // Live: the lap they're on.
    if (!this.record) return t.started ? Math.max(0, ((renderTick - t.lapStart) * 1000) / 60) : null;
    if (!t.started || t.lapIndex !== this.record.lapIndex) return null;
    return Math.max(0, ((renderTick - t.lapStart) * 1000) / 60);
  }

  liveDelta(_renderTick: number): number | null {
    return null;
  }

  ghostPose(_renderTick: number, _out: number[]): boolean {
    return false;
  }
}
